import React from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import {
    BoxSkeleton,
    CardSection,
    SkeletonLoader,
    Timeline,
    TimelineStepProps,
    useModal,
} from '@europrocurement/flexy-components';
import { Contact } from '@europrocurement/l2d-domain';
import { FormalisteJsonldFormalisteRead } from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { UseKeycloakCheckRole } from '@europrocurement/l2d-keycloak';
import ContactCard from './fragments/ContactCard';
import CommercialCard from './fragments/CommercialCard';
import FormalisteCard from './fragments/FormalisteCard';
import PriorityCard from './fragments/PriorityCard';
import UpdateDateCard from './fragments/UpdateDateCard';
import FormalisteAssignModal from '../FormalisteAssignModal/FormalisteAssignModal';

export type DossierInformationsProps = {
    steps: Array<TimelineStepProps>;
    dossierId: number;
    contactNom: string;
    contactPrenom: string;
    contactUserIdentifier: string;
    commercialNom: string;
    commercialPrenom: string;
    commercialUserIdentifier: string;
    formalisteId: number;
    formalisteNom: string;
    formalistePrenom: string;
    formalisteUserIdentifier: string;
    critical: boolean;
    dateUpdate: string;
    prescripteurContacts?: Contact[];
    availableFormalistes?: FormalisteJsonldFormalisteRead[];
    onContactChange?: () => void;
    onFormalisteChange?: (
        formaliste: FormalisteJsonldFormalisteRead,
        note?: string,
    ) => Promise<void>;
    onPriorityChange?: (critical: boolean) => Promise<void>;
    isUpdatingContact?: boolean;
    isUpdatingFormaliste?: boolean;
    isUpdatingPriority?: boolean;
    isLoadingCommercial?: boolean;
};

/**
 * Composant d'affichage des informations détaillées d'un dossier.
 * Présente la timeline de traitement, les contacts assignés, la priorité et les informations de mise à jour.
 */
const DossierInformations: React.FunctionComponent<DossierInformationsProps> = function (props) {
    const {
        steps,
        contactNom,
        contactPrenom,
        contactUserIdentifier,
        commercialNom,
        commercialPrenom,
        commercialUserIdentifier,
        formalisteId,
        formalisteNom,
        formalistePrenom,
        formalisteUserIdentifier,
        critical,
        dateUpdate,
        prescripteurContacts = [],
        availableFormalistes = [],
        onContactChange,
        onFormalisteChange,
        onPriorityChange,
        isUpdatingContact = false,
        isUpdatingFormaliste = false,
        isUpdatingPriority = false,
        isLoadingCommercial = false,
    } = props;

    const { palette, breakpoints } = useTheme();
    const { modalActions } = useModal();
    const kcCheckRole = UseKeycloakCheckRole();
    const userInterne = kcCheckRole('realm:interne');
    const downMdBreakpoint = useMediaQuery(breakpoints.down('md'));

    const timelineStyle = {
        borderBottom: `solid 1px ${palette.grey[300]}`,
        padding: downMdBreakpoint ? '32px' : '16px 32px 32px 32px',
    };

    const infosStyle = {
        display: 'grid',
        gridTemplateColumns: downMdBreakpoint ? '1fr' : 'repeat(5, 1fr)',
        gridTemplateRows: downMdBreakpoint ? 'repeat(5, 1fr)' : '1fr',
        marginTop: '16px',
        width: '100%',
    };

    const infoCardSx = {
        padding: downMdBreakpoint ? '16px 32px' : '8px 32px',
        borderRight: downMdBreakpoint ? 'none' : `solid 1px ${palette.grey[300]}`,
        '&:last-of-type': {
            borderRight: 'none',
        },
    };

    const textStyle = {
        fontSize: '13px',
        fontWeight: '500',
        lineHeight: '19.2px',
        color: palette.grey[500],
    };

    const cardHeader = (
        <Box>
            <Typography variant="l2dh6">Traitement du dossier</Typography>
        </Box>
    );

    // Props communes pour les cartes
    const baseCardProps = {
        infoCardSx,
        textStyle,
        palette,
        userInterne,
    };

    /**
     * Trouve le formaliste actuellement assigné dans la liste des formalistes disponibles
     */
    const getCurrentFormaliste = (): FormalisteJsonldFormalisteRead | null => {
        if (!formalisteNom || !formalistePrenom) return null;

        return (
            availableFormalistes.find((formaliste) => {
                const idMatch =
                    formalisteUserIdentifier &&
                    formaliste.id?.toString() === formalisteUserIdentifier?.toString();
                const nameMatch =
                    formaliste.prenom === formalistePrenom && formaliste.nom === formalisteNom;

                return idMatch || nameMatch;
            }) || null
        );
    };

    /**
     * Gère l'ouverture de la modal de formaliste avec modalActions.call()
     */
    const handleFormalisteClick = () => {
        if (availableFormalistes.length === 0) {
            console.warn('Aucun formaliste disponible');
            return;
        }

        if (!onFormalisteChange) {
            console.warn('onFormalisteChange non défini');
            return;
        }

        const currentFormaliste = getCurrentFormaliste();

        modalActions.call(
            <FormalisteAssignModal
                formalistes={availableFormalistes}
                selectedFormaliste={currentFormaliste}
                onFormalisteAssign={onFormalisteChange}
                title="Assigner un formaliste au dossier"
                submitButtonText="Assigner"
            />,
        );
    };

    const loadingCondition =
        isUpdatingContact || isUpdatingPriority || isLoadingCommercial || isUpdatingFormaliste;

    return (
        <SkeletonLoader
            isLoading={loadingCondition}
            skeleton={
                <BoxSkeleton
                    sx={{
                        height: '250px',
                        marginBottom: '16px',
                        marginTop: '16px',
                    }}
                />
            }
        >
            <CardSection
                header={cardHeader}
                sxHeader={{ padding: '24px 32px 8px 32px' }}
                sx={{ marginTop: '8px' }}
                sxContent={{ padding: '0 0 24px 0' }}
            >
                <Box sx={timelineStyle}>
                    <Timeline steps={steps} />
                </Box>

                <Box sx={infosStyle}>
                    <ContactCard
                        {...baseCardProps}
                        contactNom={contactNom}
                        contactPrenom={contactPrenom}
                        contactUserIdentifier={contactUserIdentifier}
                        prescripteurContacts={prescripteurContacts}
                        onContactChange={onContactChange}
                    />

                    <UpdateDateCard
                        {...baseCardProps}
                        dateUpdate={dateUpdate}
                    />

                    <PriorityCard
                        {...baseCardProps}
                        critical={critical}
                        onPriorityChange={onPriorityChange}
                        isUpdatingPriority={isUpdatingPriority}
                    />

                    <CommercialCard
                        {...baseCardProps}
                        commercialNom={commercialNom}
                        commercialPrenom={commercialPrenom}
                        commercialUserIdentifier={commercialUserIdentifier}
                    />

                    <FormalisteCard
                        {...baseCardProps}
                        formalisteId={formalisteId}
                        formalisteNom={formalisteNom}
                        formalistePrenom={formalistePrenom}
                        formalisteUserIdentifier={formalisteUserIdentifier}
                        availableFormalistes={availableFormalistes}
                        onFormalisteChange={handleFormalisteClick}
                    />
                </Box>
            </CardSection>
        </SkeletonLoader>
    );
};

export default DossierInformations;
